<script setup>
import { scrollToTitle } from "@/composables/commonFunction.js";

const seoTitle = "MIRASOFT - Services";
const seoDescription = "";

useHead({
  title: seoTitle,
  meta: [
    { name: "description", content: seoDescription },
    { name: "og:title", content: seoTitle },
    { name: "og:description", content: seoDescription },
    { name: "keywords", content: "Services" },
  ],
});

const services = [
  {
    id: "consultancy",
    title: "COnsultancy",
    url: "/consultancy",
    image: "/images/services/image-removebg-preview1.png",
  },
  {
    id: "technology",
    title: "TECHNOLOGY",
    url: "/technology",
    image: "/images/services/broad.png",
  },
  {
    id: "managed-services",
    title: "Managed Services",
    url: "/services",
    image: "/images/services/broad2.png",
  },
  {
    id: "design",
    title: "DESIGN",
    url: "/design",
    image: "/images/services/broad3.png",
  },
];

const caseStudies = [
  {
    id: "consultancy",
    title: "consultancy",
    des: "We employ tried-and-tested methods and tactics to create a blueprint for your digital progression, paving the way for your brand's future. This involves crafting customer experience strategies, providing consultation on both business and technology solutions, and implementing tailored solutions to meet your specific needs.",
    url: "/consultancy",
    tag: null,
    image: "/images/services/consultancy.png",
  },
  {
    id: "technology",
    title: "technology",
    des: "Mirasoft offer extensive assistance in system design and implementation to enhance business operations. Our services encompass software development, blockchain solutions, e-commerce, and CMS web development, as well as web application and mobile app development. Additionally, we specialize in IoT and AR solutions, alongside AI development, providing a holistic approach to meet diverse technological needs.",
    url: "/technology",
    tag: null,
    image: "/images/services/technology.png",
  },
  {
    id: "managed-services",
    title: "managed services", 
    des: "We assume comprehensive responsibility for managing and maintaining your IT infrastructure, freeing you to concentrate on your core priorities. Our services include technical support and training, security assessments, up-time monitoring, troubleshooting, and regular updates for websites and mobile applications, ensuring smooth and secure operation while you focus on what's important.",
    url: null,
    tag: [
      "support",
      "training",
      { icon: "/images/services/cube-white.svg" },
      "security assessments",
      "troubleshooting",
      "updates",
      { icon: "/images/services/cube-white.svg" },
      "secure",
    ],
    image: "/images/services/managed.png",
  },
  {
    id: "design",
    title: "design",
    des: "Mirasoft specialize in crafting a digital experience focused on engagement and conversation, dedicated to helping you attain your business growth objectives. Our services encompass branding and logo creation, concept art and illustration, 3D graphics, videos, motion graphics, and UI/UX design, ensuring a captivating and interactive journey for your audience while aligning with your business goals.",
    url: "/design",
    tag: null,
    image: "/images/services/design.png",
  },
];
</script>
<template>
  <div class="px-12 max-md:px-6 py-10">
    <section id="an-elite">
      <div class="bg-white p-14 pt-32 rounded-3xl max-md:p-6 max-md:pt-14 max-lg:mt-[80px]" data-aos="fade-up">
        <div>
          <p class="hidden max-md:block text-center text-[12px] font-semibold mb-4 uppercase" data-aos="fade-up">
            Try our product now
          </p>
          <h2 class="heading-1 !text-black leading-[78px] max-md:leading-8" data-aos="fade-up">
            An elite <br />
            ecosystem for
          </h2>
          <h2 class="heading-1 !text-[#F67D30] leading-[78px] max-md:leading-8" data-aos="fade-up" data-aos-delay="100">
            entrepreneurs
          </h2>
        </div>
        <div class="mt-[152px] max-lg:mt-[72px] flex justify-center gap-4 max-lg:flex-col" data-aos="fade-up">
          <div
            class="flex-1 bg-[#F1F1F1] text-center p-10 rounded-[20px] transform transition-all lg:hover:scale-125 lg:hover:mx-14 max-lg:p-6 max-lg:mx-0 max-lg:my-2 max-lg:relative"
            v-for="service in services"
            :key="service.id"
            @click="scrollToTitle(service.id)"
          >
            <nuxt-picture
              :src="service.image"
              :img-attrs="{ alt: service.title, class: 'h-[128px] object-cover mx-auto max-lg:h-[60px] max-lg:mx-0' }"
            />
            <div class="subtitle-2 font-bold !text-black uppercase mt-3 max-lg:text-left">{{ service.title }}</div>
            <div
              class="mx-auto w-[48px] h-[48px] border border-[rgba(11,11,11,0.16)] rounded-[50%] justify-center items-center hidden max-lg:flex absolute bottom-6 right-6"
            >
              <nuxt-picture
                loading="lazy"
                src="/images/services/arrow-down-black.svg"
                width="24px"
                height="24px"
                :imgAttrs="{ alt: 'arrow-down' }"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="case-studies" class="mt-[96px] max-md:mt-5">
      <div v-for="(studies, index) in caseStudies" :key="studies.id" class="mt-[96px] max-lg:mt-6" data-aos="fade-up">
        <div :id="studies.id" class="heading-1" data-aos="fade-up">{{ studies.title }}</div>
        <div
          class="flex gap-8 mt-10 max-lg:flex-col max-lg:gap-2 max-lg:mt-2"
          :class="index % 2 !== 0 && 'flex-row-reverse'"
        >
          <div class="basis-[55%] rounded-3xl overflow-hidden" data-aos="fade-up" data-aos-delay="100">
            <nuxt-picture
              :src="studies.image"
              :img-attrs="{ alt: studies.title, class: 'w-full h-full object-cover' }"
            />
          </div>
          <div class="basis-[45%] flex flex-col gap-6 max-lg:gap-2">
            <div
              class="flex-1 bg-white p-12 text-[18px] rounded-3xl !text-black description-1 max-lg:p-6"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              {{ studies.des }}
            </div>
            <div
              v-if="studies.url"
              class="bg-[#F67D30] p-14 rounded-3xl max-xl:p-10 max-lg:p-5"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <nuxt-link :to="studies.url" class="flex justify-center items-center gap-4">
                <div class="subtitle-1 uppercase">CASE STUDies</div>
                <div class="w-[48px] h-[48px] border border-white rounded-[50%] flex justify-center items-center">
                  <nuxt-picture
                    loading="lazy"
                    src="/images/arrow-down.svg"
                    width="24px"
                    height="24px"
                    :imgAttrs="{ alt: 'arrow-down' }"
                  />
                </div>
              </nuxt-link>
            </div>
            <div
              v-if="studies.tag"
              class="bg-[#F67D30] p-8 rounded-3xl flex flex-wrap gap-2 items-center"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div v-for="(tag, index) in studies.tag" :key="index">
                <div v-if="typeof tag === 'string'" class="border border-white px-[27.5px] py-[10px] rounded-[30px]">
                  <span class="text-white uppercase leading-none">{{ tag }}</span>
                </div>
                <div v-else class="text-white border border-white p-[9px] rounded-full">
                  <nuxt-picture :src="tag.icon" :img-attrs="{ alt: tag, class: 'w-[24px] h-[24px] mx-auto' }" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <Excellence class="mt-[96px]" />
  </div>
</template>
